import React, { useState, useEffect, useCallback } from 'react';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Package, Truck, Wrench, CheckCircle2, AlertTriangle, Search,
  ChevronDown, ChevronUp, ExternalLink, Clock, MapPin
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-800 border-amber-200', dot: 'bg-amber-500' },
  processing: { label: 'Processing', color: 'bg-blue-100 text-blue-800 border-blue-200', dot: 'bg-blue-500' },
  in_transit: { label: 'In Transit', color: 'bg-violet-100 text-violet-800 border-violet-200', dot: 'bg-violet-500' },
  delivered: { label: 'Delivered', color: 'bg-cyan-100 text-cyan-800 border-cyan-200', dot: 'bg-cyan-500' },
  installation_scheduled: { label: 'Install Scheduled', color: 'bg-indigo-100 text-indigo-800 border-indigo-200', dot: 'bg-indigo-500' },
  installed: { label: 'Installed', color: 'bg-emerald-100 text-emerald-800 border-emerald-200', dot: 'bg-emerald-500' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800 border-green-200', dot: 'bg-green-500' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800 border-red-200', dot: 'bg-red-500' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color}`} data-testid={`status-badge-${status}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function KPICard({ icon: Icon, label, value, color, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex-1 min-w-[160px] rounded-xl border p-4 text-left transition-all duration-200 hover:shadow-md ${
        active ? 'ring-2 ring-primary border-primary bg-primary/5' : 'border-border bg-card hover:border-primary/30'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <span className="text-2xl font-bold font-[var(--font-heading)]">{value}</span>
      </div>
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
    </button>
  );
}

export default function OrdersManagementPage() {
  const { token, user } = useAuthStore();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const isAdmin = user?.role === 'super_admin' || user?.role === 'iso';

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/equipment/orders/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setDashboard(data);
    } catch (err) {
      toast.error('Failed to load orders dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const handleStatusUpdate = async (orderId, updates) => {
    setUpdating(true);
    try {
      const res = await fetch(`${API}/api/equipment/orders/${orderId}/details`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Update failed');
      }
      toast.success('Order updated');
      await fetchDashboard();
      setDetailOpen(false);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="orders-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  const kpis = dashboard?.kpis || {};
  const orders = dashboard?.recent_orders || [];
  const attention = dashboard?.attention_needed || [];

  // Filter & search
  const filtered = orders.filter(o => {
    if (statusFilter !== 'all') {
      if (statusFilter === 'pending') return ['pending', 'processing'].includes(o.status);
      if (statusFilter === 'installation') return ['delivered', 'installation_scheduled'].includes(o.status);
      if (statusFilter === 'completed') return ['installed', 'completed'].includes(o.status);
      return o.status === statusFilter;
    }
    return true;
  }).filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    const items = o.items || [];
    return items.some(i => i.product_name?.toLowerCase().includes(s)) ||
      o.id?.toLowerCase().includes(s) ||
      o.tracking_number?.toLowerCase().includes(s) ||
      o.notes?.toLowerCase().includes(s);
  });

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    let av = a[sortField], bv = b[sortField];
    if (sortField === 'total') { av = av || 0; bv = bv || 0; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />;
  };

  return (
    <div className="space-y-6" data-testid="orders-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold font-[var(--font-heading)]">Orders Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track equipment orders, shipments, and installations
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="flex flex-wrap gap-3" data-testid="orders-kpi-cards">
        <KPICard icon={Package} label="Pending" value={kpis.pending || 0}
          color="bg-amber-100 text-amber-700" testId="kpi-pending"
          active={statusFilter === 'pending'} onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')} />
        <KPICard icon={Truck} label="In Transit" value={kpis.in_transit || 0}
          color="bg-violet-100 text-violet-700" testId="kpi-in-transit"
          active={statusFilter === 'in_transit'} onClick={() => setStatusFilter(statusFilter === 'in_transit' ? 'all' : 'in_transit')} />
        <KPICard icon={Wrench} label="Installation" value={kpis.installation || 0}
          color="bg-indigo-100 text-indigo-700" testId="kpi-installation"
          active={statusFilter === 'installation'} onClick={() => setStatusFilter(statusFilter === 'installation' ? 'all' : 'installation')} />
        <KPICard icon={CheckCircle2} label="Completed" value={kpis.completed || 0}
          color="bg-emerald-100 text-emerald-700" testId="kpi-completed"
          active={statusFilter === 'completed'} onClick={() => setStatusFilter(statusFilter === 'completed' ? 'all' : 'completed')} />
      </div>

      {/* Attention Needed */}
      {attention.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid="attention-needed-section">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <h3 className="text-sm font-semibold text-amber-800">Attention Needed ({attention.length})</h3>
          </div>
          <div className="space-y-2">
            {attention.map((a, i) => (
              <div key={i} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-amber-100" data-testid={`attention-item-${i}`}>
                <div className="flex items-center gap-3">
                  <StatusBadge status={a.status} />
                  <span className="text-sm font-medium">{a.merchant_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-amber-700">{a.reason}</span>
                  <span className="text-xs text-muted-foreground font-mono">${(a.total || 0).toFixed(2)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3" data-testid="orders-filters">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search orders by product, ID, or tracking..."
            value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9" data-testid="orders-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="orders-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_transit">In Transit</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
            <SelectItem value="installation">Installation</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="orders-table-container">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="text-left px-4 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort('created_at')}>
                  <div className="flex items-center gap-1">Date <SortIcon field="created_at" /></div>
                </th>
                <th className="text-left px-4 py-3 font-medium">Items</th>
                <th className="text-left px-4 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort('status')}>
                  <div className="flex items-center gap-1">Status <SortIcon field="status" /></div>
                </th>
                <th className="text-left px-4 py-3 font-medium">Tracking</th>
                <th className="text-right px-4 py-3 font-medium cursor-pointer select-none" onClick={() => toggleSort('total')}>
                  <div className="flex items-center justify-end gap-1">Total <SortIcon field="total" /></div>
                </th>
                <th className="text-center px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-12 text-muted-foreground">
                    No orders found
                  </td>
                </tr>
              ) : sorted.map(order => {
                const items = order.items || [];
                const mainItem = items[0] || {};
                const dateStr = order.created_at ? new Date(order.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

                return (
                  <tr key={order.id} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors" data-testid={`order-row-${order.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                        <span className="text-sm">{dateStr}</span>
                      </div>
                      <span className="text-xs text-muted-foreground font-mono mt-0.5 block">{order.id?.slice(-8)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {mainItem.image_url ? (
                          <img src={mainItem.image_url} alt="" className="w-8 h-8 rounded object-cover border" />
                        ) : (
                          <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                            <Package className="w-4 h-4 text-muted-foreground" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-sm leading-tight">{mainItem.product_name || 'Equipment'}</p>
                          {items.length > 1 && (
                            <p className="text-xs text-muted-foreground">+{items.length - 1} more item{items.length > 2 ? 's' : ''}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="px-4 py-3">
                      {order.tracking_number ? (
                        <div className="flex items-center gap-1.5">
                          <MapPin className="w-3 h-3 text-muted-foreground" />
                          <span className="text-xs font-mono">{order.tracking_number}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-semibold font-mono">${(order.total || 0).toFixed(2)}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => { setSelectedOrder(order); setDetailOpen(true); }}
                        data-testid={`order-view-btn-${order.id}`}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/30 text-xs text-muted-foreground">
          Showing {sorted.length} of {orders.length} orders
        </div>
      </div>

      {/* Order Detail Dialog */}
      <OrderDetailDialog
        order={selectedOrder}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        isAdmin={isAdmin}
        updating={updating}
        onUpdate={handleStatusUpdate}
      />
    </div>
  );
}

function OrderDetailDialog({ order, open, onClose, isAdmin, updating, onUpdate }) {
  const [newStatus, setNewStatus] = useState('');
  const [tracking, setTracking] = useState('');
  const [carrier, setCarrier] = useState('');
  const [installDate, setInstallDate] = useState('');
  const [installNotes, setInstallNotes] = useState('');

  useEffect(() => {
    if (order) {
      setNewStatus(order.status || '');
      setTracking(order.tracking_number || '');
      setCarrier(order.shipping_carrier || '');
      setInstallDate(order.installation_date || '');
      setInstallNotes(order.installation_notes || '');
    }
  }, [order]);

  if (!order) return null;

  const items = order.items || [];
  const dateStr = order.created_at ? new Date(order.created_at).toLocaleString() : '—';

  const hasChanges = newStatus !== (order.status || '') ||
    tracking !== (order.tracking_number || '') ||
    carrier !== (order.shipping_carrier || '') ||
    installDate !== (order.installation_date || '') ||
    installNotes !== (order.installation_notes || '');

  const handleSave = () => {
    const updates = {};
    if (newStatus !== (order.status || '')) updates.status = newStatus;
    if (tracking !== (order.tracking_number || '')) updates.tracking_number = tracking;
    if (carrier !== (order.shipping_carrier || '')) updates.shipping_carrier = carrier;
    if (installDate !== (order.installation_date || '')) updates.installation_date = installDate;
    if (installNotes !== (order.installation_notes || '')) updates.installation_notes = installNotes;
    onUpdate(order.id, updates);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="order-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Order Details
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {/* Order Info */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-muted-foreground">ID: {order.id?.slice(-8)}</span>
            <StatusBadge status={order.status} />
          </div>
          <div className="text-xs text-muted-foreground">Created: {dateStr}</div>

          {/* Items */}
          <div className="rounded-lg border border-border overflow-hidden">
            <div className="px-3 py-2 bg-muted/40 text-xs font-medium">Items ({items.length})</div>
            {items.map((item, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 border-t border-border" data-testid={`order-item-${i}`}>
                <div className="flex items-center gap-2">
                  {item.image_url ? (
                    <img src={item.image_url} alt="" className="w-8 h-8 rounded border object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-muted flex items-center justify-center">
                      <Package className="w-4 h-4 text-muted-foreground" />
                    </div>
                  )}
                  <div>
                    <p className="text-sm font-medium">{item.product_name}</p>
                    <p className="text-xs text-muted-foreground">Qty: {item.quantity} | {item.platform}</p>
                  </div>
                </div>
                <span className="text-sm font-mono font-medium">${(item.line_total || 0).toFixed(2)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between px-3 py-2 border-t border-border bg-muted/20">
              <span className="text-sm font-semibold">Total</span>
              <span className="text-sm font-bold font-mono">${(order.total || 0).toFixed(2)}</span>
            </div>
          </div>

          {/* Editable Fields (Admin Only) */}
          {isAdmin && (
            <div className="space-y-3 pt-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="order-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground mb-1 block">Carrier</label>
                  <Input value={carrier} onChange={e => setCarrier(e.target.value)} placeholder="UPS, FedEx..." data-testid="order-carrier-input" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Tracking Number</label>
                <Input value={tracking} onChange={e => setTracking(e.target.value)} placeholder="Enter tracking number" data-testid="order-tracking-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Installation Date</label>
                <Input type="date" value={installDate} onChange={e => setInstallDate(e.target.value)} data-testid="order-install-date-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Installation Notes</label>
                <Input value={installNotes} onChange={e => setInstallNotes(e.target.value)} placeholder="Site prep notes, contact info..." data-testid="order-install-notes-input" />
              </div>
            </div>
          )}

          {/* Read-only info for non-admins */}
          {!isAdmin && order.tracking_number && (
            <div className="rounded-lg border border-border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Tracking</span>
                <span className="text-sm font-mono">{order.tracking_number}</span>
              </div>
              {order.shipping_carrier && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Carrier</span>
                  <span className="text-sm">{order.shipping_carrier}</span>
                </div>
              )}
              {order.installation_date && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Install Date</span>
                  <span className="text-sm">{order.installation_date}</span>
                </div>
              )}
            </div>
          )}

          {order.notes && (
            <div className="text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
              <strong>Notes:</strong> {order.notes}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="order-detail-close-btn">Close</Button>
          {isAdmin && hasChanges && (
            <Button onClick={handleSave} disabled={updating} data-testid="order-save-btn">
              {updating ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
